import React, { FC, useEffect, useState } from 'react';
import styles from './EditUserModal.module.css';
import { RoleType, RoleTypeLabel, RoleTypeList, RoleTypeListAdmin, User, UserAttributes } from '../../@types/Auth';
import { toast } from 'react-toastify';
import Shape from '../../@types/Shape';
import SelectOption from '../../@types/SelectOption';
import Modal from '../common/Modal/Modal';
import { FormProvider, useForm } from 'react-hook-form';
import TextField from '../common/Form/FormFields/TextField/TextField';
import LoadingButton from '../common/LoadingButton/LoadingButton';
import Button from '../common/Button';
import SelectOptions from '../common/Form/FormFields/Select/Select';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Stack, Typography } from '@mui/material';
import axios from '../../api/axios';
import useAuth from '../../hooks/useAuth';
import SplitColumnWrapper from '../common/Form/SplitColumnWrapper/SplitColumnWrapper';
import CheckBox from '../common/Form/FormFields/CheckBox/CheckBox';
import { isMadJaxAdmin } from '../../utils/isMadJaxAdmin';
import { isDealerAdmin } from '../../utils/isDealerAdmin';
import flatten from 'flat';

interface EditUserModalProps {
  user: User | undefined;
  closeModal: () => void;
  setUsers?: React.Dispatch<React.SetStateAction<User[]>>;
  isVisible: boolean;
}

const EditUserModal: FC<EditUserModalProps> = ({ user, closeModal, setUsers, isVisible }) => {
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);

  const onSubmit = async (user: User) => {
    try {
      setLoading(true);
      await axios.patch(`/user`, user);
      toast.success(`Updated user ${user?.email}`);
      if (setUsers) {
        setUsers((users) => {
          const userIndex = users.findIndex((u) => u.sk === user.sk);
          users[userIndex] = user;
          return [...users];
        });
      }
      closeModal();
    } catch (error: any) {
      console.log(error);
      toast.error(error.response.data.error);
    } finally {
      setLoading(false);
    }
  };

  const onError = (errors: any) => {
    console.log(errors);
    const errorsFlattened: any = flatten(errors);
    const requiredErrors = Object.fromEntries(Object.entries(errorsFlattened).filter(([key, value]) => key.includes('.type')));
    if (Object.entries(requiredErrors).length <= 2) {
      for (const key in requiredErrors) {
        let keySplit = key.split('.');
        keySplit.pop();
        let formField = '';
        for (const fieldPart of keySplit) {
          formField += fieldPart.split(/([A-Z][a-z]+)/).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
        }
        toast.error(`Please check [${formField.trim()}] field for errors`);
      }
    } else {
      toast.error('Please check form for errors');
    }
  };

  const editUserSchema = yup.object<Shape<User>>({
    pk: yup.string().required(),
    sk: yup.string().required(),
    email: yup.string()
      .strict()
      .trim('email address cannot include leading or trailing spaces')
      .email().email('Please enter a valid email address')
      .required(`Please enter an email address`),
    attributes: yup.object<Shape<UserAttributes>>({
      accountStatus: yup.string().required('Please select the user account status'),
      firstName: yup.string().required('Please enter a user firstname'),
      lastName: yup.string().required('Please enter a user lastname'),
      phone: yup.string().optional(),
      type: yup.string().required('Please select the permission level for the user'),
      tempPassword: yup.string().optional(),
      leadsContact: yup.boolean().optional()
    }),
  });

  const accountStatusOptions: SelectOption[] = [
    { label: 'Registered', value: 'registered' },
    { label: 'Email Confirmation', value: 'email confirmation' },
    { label: 'Unregistered', value: 'unregistered' },
  ];

  const userRoles: RoleTypeLabel[] = currentUser?.attributes?.type !== RoleType.Admin ? RoleTypeList : RoleTypeListAdmin;

  const methods = useForm<User>({
    resolver: yupResolver(editUserSchema)
  });

  const { handleSubmit, watch, reset } = methods;

  const [roleDescription, setRoleDescription] = useState<string>('');
  const selectedUserRole = watch('attributes.type');

  useEffect(() => {
    const role = userRoles.find((userRole) => userRole.value === selectedUserRole);
    setRoleDescription(role?.description ?? '');
  }, [selectedUserRole, setRoleDescription]);

  /* 
    Using reset here to populate default values
    as user could be undefined and the form will cache undefined
    as default values and not rerender.  So when the user
    props are passed in we rerender with reset
  */
  useEffect(() => {

    reset(user);

  }, [user]);

  return (
    <>
      {isVisible &&
        <Modal title={`Edit User [${user?.email}]`} closeModal={closeModal}>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit, onError)}>
              <Stack direction="column" spacing={2}>
                <SplitColumnWrapper>
                  <TextField name="attributes.firstName" label="First Name" />
                  <TextField name="attributes.lastName" label="Last Name" />
                </SplitColumnWrapper>
                <SplitColumnWrapper>
                  <TextField name="email" label="Email" />
                  <TextField name="attributes.phone" label="Phone" />
                </SplitColumnWrapper>
                <TextField name="attributes.tempPassword" label="Temp Password" />
                <SelectOptions name="attributes.accountStatus" label="Account Status" disabled={!isDealerAdmin(currentUser) && !isMadJaxAdmin(currentUser)} options={accountStatusOptions} />
                <Stack direction="column">
                  <SelectOptions name="attributes.type" label="Permission Level" options={userRoles} disabled={!isDealerAdmin(currentUser) && !isMadJaxAdmin(currentUser)} />
                  <Typography>{roleDescription}</Typography>
                </Stack>
                <CheckBox
                  name="attributes.leadsContact"
                  label="Leads Contact"
                  isTrueFalse={true}
                  disabled={!isDealerAdmin(currentUser) || !isMadJaxAdmin(currentUser)}
                  defaultValue={0} />
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <LoadingButton loading={loading} className={styles.SubmitButton} type="submit">Submit</LoadingButton>
                  <Button className={styles.CancelButton} onClick={closeModal} type="button">Cancel</Button>
                </Stack>
              </Stack>
            </form>
          </FormProvider>
        </Modal>
      }
    </>
  )
}

export default EditUserModal;
